import { Box, Skeleton, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import BenchmarkDeltaCell, { DeltaHeaderDot } from 'views/benchmark-analytics/components/benchmark-delta-cell';
import SeriesMarker from 'views/benchmark-analytics/components/series-marker';
import { COMPANY_SLOT, MARKET_SLOT } from 'views/benchmark-analytics/constants/benchmark-series';
import { useBenchmarkFormatters } from 'views/benchmark-analytics/hooks/use-benchmark-formatters';
import { deltaSentiment } from 'views/benchmark-analytics/utils/benchmark-math';
import { GROWTH_DECIMALS } from '../constants/growth';
import { useGrowthState } from '../context/growth-context';
import { GrowthTableRow } from '../types/growth-table.type';

const MESSAGE_ROOT = 'benchmarkAnalytics.growthPage';

const SeriesHeader: React.FC<{ marker: React.ReactNode; label: string }> = ({ marker, label }) => (
  <Box display="flex" alignItems="center" gap={2} minWidth={0}>
    {marker}
    <Typography variant="body2" fontWeight={600} noWrap title={label}>
      {label}
    </Typography>
  </Box>
);

export const useGrowthTableColumns = (): GridColDef<GrowthTableRow>[] => {
  const intl = useIntl();
  const { farmSeries, filters } = useGrowthState();
  const { formatValue, formatDelta, formatPeriodLabel } = useBenchmarkFormatters();

  return useMemo<GridColDef<GrowthTableRow>[]>(() => {
    const valueColumn = (
      field: string,
      slot: number,
      label: string,
      getValue: (row: GrowthTableRow) => number | null,
      renderCell?: GridColDef<GrowthTableRow>['renderCell']
    ): GridColDef<GrowthTableRow> => ({
      field,
      headerName: label,
      flex: 1,
      minWidth: 190,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (_value, row) => getValue(row),
      valueFormatter: (value: number | null) => formatValue(value, GROWTH_DECIMALS),
      renderHeader: () => <SeriesHeader marker={<SeriesMarker slot={slot} />} label={label} />,
      renderCell,
    });

    const deltaLabel = intl.formatMessage({ id: `${MESSAGE_ROOT}.table.delta` });
    const noData = intl.formatMessage({ id: `${MESSAGE_ROOT}.chart.noData` });
    const farmColumns = farmSeries.map((farm, farmIndex) => {
      const label = intl.formatMessage({ id: `${MESSAGE_ROOT}.table.farmGrowth` }, { farm: farm.name });
      return valueColumn(
        `farm-${farm.globalSiteId}`,
        farm.slot,
        farm.status === 'noData' ? `${label} ${noData}` : label,
        (row) => row.farms[farmIndex],
        farm.status === 'loading' ? () => <Skeleton variant="text" width="60%" /> : undefined
      );
    });

    return [
      {
        field: 'period',
        headerName: intl.formatMessage({ id: `${MESSAGE_ROOT}.table.period` }),
        minWidth: 140,
        flex: 0.7,
        sortable: false,
        valueFormatter: (value: string) => formatPeriodLabel(value, filters.grouping),
      },
      valueColumn('company', COMPANY_SLOT, intl.formatMessage({ id: `${MESSAGE_ROOT}.table.company` }), (row) => row.company),
      valueColumn('market', MARKET_SLOT, intl.formatMessage({ id: `${MESSAGE_ROOT}.table.market` }), (row) => row.market),
      {
        field: 'delta',
        headerName: deltaLabel,
        flex: 1,
        minWidth: 150,
        sortable: false,
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_value, row) => row.delta,
        renderHeader: () => <SeriesHeader marker={<DeltaHeaderDot />} label={deltaLabel} />,
        renderCell: ({ row }) => (
          <BenchmarkDeltaCell
            text={formatDelta(row.delta, GROWTH_DECIMALS)}
            delta={row.delta}
            sentiment={deltaSentiment(row.delta, GROWTH_DECIMALS)}
          />
        ),
      },
      ...farmColumns,
    ];
  }, [intl, farmSeries, filters.grouping, formatValue, formatDelta, formatPeriodLabel]);
};
